/**
 * Linear tool-call timeline for the sandbox deep-agent — an ordered,
 * top-to-bottom list of every tool the ReAct loop dispatched, one row per
 * turn: icon + humanized action + target + outcome pill + (optional)
 * reasoning. Fed by `state.v2Tree.toolTimeline`, which accumulates
 * `V2_NODE_TOOL` actions (the structured sibling of `v2:node:activity` — see
 * `DeepAgentToolEvent` in `packages/sandbox-ai-v2/src/deepAgent/deps.ts`).
 *
 * This is deliberately a FULL, ordered history — distinct from the scattered,
 * capped, per-node `activityLog` the tree view (`V2PipelineTreeView`) renders
 * — so a human can read the whole build top-to-bottom in one place.
 *
 *   1  📂 Read file      shared/constants.ts                      ok
 *   2  📁 Create file    shared/Game.ts                 created 812 chars
 *   3  🔧 Edit           server/systems/GameServerSystem.ts             ok
 *      "tighten the collision check so…"
 *   4  🔁 Run tests                                    8 test(s), 1 failing
 *   5  🚀 Finish                                            build complete
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode, KeyboardEvent } from 'react';
import { useAgent } from '../agentContextCore';
import type { V2ToolCall } from '../types';
import {
  IconFolderOpen,
  IconFolder,
  IconSearch,
  IconClipboard,
  IconChat,
  IconGear,
  IconWrench,
  IconRefresh,
  IconRocket,
} from './icons';

type IconComponent = (p: { size?: number | string; className?: string }) => ReactNode;

/** action name -> icon, per the AgentToolTimeline spec. Unlisted actions
 *  (and the synthetic `(unparsed)` action) fall back to IconWrench. */
const ACTION_ICON: Record<string, IconComponent> = {
  read_file: IconFolderOpen,
  grep: IconSearch,
  search_framework: IconSearch,
  list_symbols: IconClipboard,
  get_design_spec: IconClipboard,
  get_test_results: IconClipboard,
  get_coverage: IconClipboard,
  create_file: IconFolder,
  edit: IconWrench,
  run_tests: IconRefresh,
  type_check: IconGear,
  get_type_errors: IconGear,
  finish: IconRocket,
  write_design_spec: IconChat,
};

function iconFor(action: string): IconComponent {
  return ACTION_ICON[action] ?? IconWrench;
}

/** 'run_tests' -> 'Run tests'; '(unparsed)' passes through unchanged (it's
 *  already a human-readable marker, not a snake_case action name). */
function humanizeAction(action: string): string {
  if (!action || action.startsWith('(')) return action;
  const [first, ...rest] = action.split('_');
  const head = first.charAt(0).toUpperCase() + first.slice(1);
  return rest.length === 0 ? head : `${head} ${rest.join(' ')}`;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function AgentToolTimeline() {
  const { state } = useAgent();
  const timeline = state.v2Tree?.toolTimeline ?? [];
  const active = !!state.v2Tree?.active;
  const [expanded, setExpanded] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the newest row while the run is active — mirrors the
  // AgentChatPanel messages-end-ref pattern (scrollIntoView on new content).
  useEffect(() => {
    if (active && expanded) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [timeline.length, active, expanded]);

  if (timeline.length === 0) return null;

  return (
    <div className="v2-tooltl" data-testid="agent-tool-timeline">
      <button
        type="button"
        className="v2-tooltl-header"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        data-testid="agent-tool-timeline-toggle"
      >
        <span className="v2-tooltl-caret" aria-hidden>{expanded ? '▾' : '▸'}</span>
        <span className="v2-tooltl-title">Agent actions</span>
        <span className="v2-tooltl-badge">{timeline.length}</span>
      </button>
      {expanded && (
        <div className="v2-tooltl-body" data-testid="agent-tool-timeline-body">
          {timeline.map((call, i) => (
            <AgentToolTimelineRow key={call.seq} call={call} isLast={i === timeline.length - 1} />
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
}

/** Splits a (possibly multi-line) detail string into its summary (first line,
 *  shown collapsed + used as the status pill) and child lines (rendered as
 *  collapsible child nodes on expand — e.g. the individual failing tests under
 *  a run_tests row, or the errors under a type_check row). */
function splitDetail(detail: string | undefined): { summary: string | undefined; children: string[] } {
  if (!detail) return { summary: undefined, children: [] };
  const [summary, ...children] = detail.split('\n');
  return { summary, children };
}

function AgentToolTimelineRow({ call, isLast }: { call: V2ToolCall; isLast: boolean }) {
  const Icon = iconFor(call.action);
  const { summary, children } = splitDetail(call.detail);
  const thought = call.thought?.trim();
  // A row is a collapsible NODE when it has hierarchy to reveal: child detail
  // lines (failing tests / type errors) or a thought longer than its inline
  // preview. Rows with nothing extra stay flat (no caret, not clickable). The
  // collapsed view never HIDES anything shown before — expanding only ADDS the
  // full thought + the child lines.
  const previewLen = 140;
  const thoughtOverflows = !!thought && thought.length > previewLen;
  const expandable = children.length > 0 || thoughtOverflows;
  const [open, setOpen] = useState(false);

  return (
    <div className={`v2-tooltl-row${expandable ? ' expandable' : ''}`} data-testid="agent-tool-row">
      <div className="v2-tooltl-rail">
        <span className="v2-tooltl-seq">{call.seq}</span>
        <span className={`v2-tooltl-icon status-${call.status}`}>
          <Icon />
        </span>
        {!isLast && <span className="v2-tooltl-connector" aria-hidden />}
      </div>
      <div className="v2-tooltl-content">
        <div
          className="v2-tooltl-primary"
          {...(expandable
            ? {
                role: 'button' as const,
                tabIndex: 0,
                'aria-expanded': open,
                onClick: () => setOpen((o) => !o),
                onKeyDown: (e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpen((o) => !o);
                  }
                },
              }
            : {})}
        >
          {expandable && (
            <span className="v2-tooltl-rowcaret" aria-hidden>
              {open ? '▾' : '▸'}
            </span>
          )}
          <span className="v2-tooltl-action">{humanizeAction(call.action)}</span>
          {call.target && <span className="v2-tooltl-target">{call.target}</span>}
          {summary && <span className={`v2-tooltl-pill status-${call.status}`}>{summary}</span>}
          {children.length > 0 && !open && (
            <span className="v2-tooltl-childcount">{children.length}</span>
          )}
        </div>
        {/* Inline thought preview — always visible so the agent's reasoning is
            never lost; the full text is revealed on expand when it overflows. */}
        {thought && !open && <div className="v2-tooltl-thought">{truncate(thought, previewLen)}</div>}
        {open && (
          <div className="v2-tooltl-children" data-testid="agent-tool-children">
            {thought && <div className="v2-tooltl-thought full">{thought}</div>}
            {children.map((line, i) => (
              <div key={i} className="v2-tooltl-childline">
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
